<template>
  <div id="search-results-container">
    <BackComponent />

    <LoaderComponent v-if="!loaded" />
    <div v-else>
      <div id="search-header-container">
        <div id="search-info-container">
          <!-- Number of results found out of total number of objects -->
          <div id="search-info">
            <p v-if="resultsExist">
              Results of your search | {{ pageInfo.total }} record(s) out of
              {{ total }} objects
            </p>
            <p v-else>Results of your search | <span>No results</span></p>
          </div>
        </div>
        <!-- In-page search (same function as banner search) -->
        <div id="results-search-container">
          <form @submit.prevent>
            <input
              id="results-search-input"
              type="search"
              v-model="searchInput"
              placeholder="Enter your search term(s) here"
            />
            <div id="search-buttons-container">
              <button
                id="results-search-submit"
                class="results-search-button"
                name="search"
                type="submit"
                @click="submit($event)"
              >
                Search
              </button>
              <!-- "Reset" button shows all objects -->
              <button
                id="results-search-reset"
                class="results-search-button"
                name="reset"
                type="submit"
                @click="submit($event)"
              >
                Reset
              </button>
            </div>
          </form>
          <div id="how-to-link-container">
            <router-link :to="{ name: 'search-how-to' }">
              Search Term Tips
            </router-link>
          </div>
        </div>
      </div>

      <div
        id="top-page-navigation"
        class="page-navigation-wrapper"
        v-if="displayPageNavigation"
      >
        <PagesComponent
          :pageInfo="pageInfo"
          :navigatePage="navigatePage"
          :navigatePageDirect="navigatePageDirect"
        ></PagesComponent>
      </div>

      <!-- Show only if there are results available -->
      <div id="results-wrapper" v-if="resultsExist">
        <!-- Larger picture + details of currently focused object -->
        <!-- Show this section only on larger screens -->
        <div id="results-main-object" v-if="!lessThan974">
          <div id="main-object-photo-container">
            <img id="main-object-image" :src="mainObject.imagePath" />
          </div>
          <div id="main-object-description">
            <div id="main-object-name-date">
              <p class="bold" v-html="mainObject.name"></p>
              <!-- <p>{{ mainObject.objectDate }}</p> -->
              <p v-html="mainObject.objectDate"></p>
            </div>
            <div id="main-object-partner-location">
              <p class="bold">{{ mainObject.partnerName }}</p>
              <p>{{ mainObject.location }}, {{ mainObject.countryName }}</p>
            </div>
          </div>
        </div>

        <!-- Thumbnail results of search -->
        <div id="results-container">
          <div
            class="result-wrapper"
            v-for="(result, index) in editedResults"
            :key="result.imagePath"
            :class="`result-${index + 1}`"
          >
            <div class="result">
              <router-link
                :to="{
                  name: 'database-item',
                  params: {
                    pathMatch: result.dbUid.split('/').slice(1),
                    language: result.languageId
                  },
                }"
                class="result-image-wrapper"
              >
                <!-- Hover changes "main" object display -->
                <img
                  class="result-image"
                  :src="result.imagePath"
                  @mouseover="mainObject = result"
                />
              </router-link>
              <div class="result-details">
                <router-link
                  :to="{
                    name: 'database-item',
                    params: {
                      pathMatch: result.dbUid.split('/').slice(1),
                      language: result.languageId
                    },
                  }"
                  class="result-image-wrapper"
                >
                  <span
                    class="bold"
                    @mouseover="mainObject = result"
                    v-html="result.name"
                  ></span>
                  <span>, {{ result.location }}, {{ result.countryName }}</span>
                </router-link>
                <!-- <p class="italic">{{ result.objectDate }}</p> -->
                <p class="italic" v-html="result.objectDate"></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Show if there are no results -->
      <div id="search-no-results" v-else>
        <div id="no-results-text">
          <p>No results found.</p>
          <p>
            Change or add to your search term(s) using the search bar, or click
            <button name="reset" @click="submit($event)">here</button> to see
            all objects available in this database.
          </p>
          <p>
            Tips: Check your search term(s) for typos, Your search may be too
            narrow to find results - try search terms that are more general
            rather than specific (subject or material only, with fewer
            adjectives).
          </p>
        </div>
      </div>

      <div
        id="bottom-page-navigation"
        class="page-navigation-wrapper"
        v-if="displayPageNavigation"
      >
        <PagesComponent
          :pageInfo="pageInfo"
          :navigatePage="navigatePage"
          :navigatePageDirect="navigatePageDirect"
        ></PagesComponent>
      </div>
      <!-- Extra padding for page when there are not multiple pages -->
      <div id="results-padding" v-else></div>
    </div>
  </div>
</template>



<script>
import BackComponent from "./BackComponent.vue";
import LoaderComponent from "./LoaderComponent.vue";
import PagesComponent from "./PagesComponent.vue";
import media from "../mixins/media.js";
import pageDisplay from "../mixins/pageDisplay.js";
import characters from "../mixins/characters.js";
export default {
  name: "SearchResults",
  components: {
    BackComponent: BackComponent,
    LoaderComponent: LoaderComponent,
    PagesComponent: PagesComponent,
  },
  mixins: [media, pageDisplay, characters],
  data() {
    return {
      lessThan974: window.matchMedia("(max-width: 974px)").matches,
      loaded: false,
      searchInput: "",
      resultsExist: true,
      displayPageNavigation: false,
      results: {},
      mainObject: {},
      pageInfo: {},
      pages: {},
      total: "",
    };
  },
  computed: {
    // Remove extra period (punctuation)
    editedResults: function () {
      let data = this.results;
      for (let key in data) {
        let name = data[key].name;
        if (name.slice(-1) === ".") {
          data[key].name = name.substring(0, name.length - 1);
        }
        let date = data[key].objectDate;
        if (this.charactersOver80(date)) {
          data[key].objectDate = this.cutOff(date);
        }
      }
      return data;
    },
  },
  watch: {
    $route() {
      this.getObjects();
      // Display current search in search box
      this.searchInput = this.setSearchInput(this.$route.query.q);
    },
  },
  methods: {
    // Get total number of objects in project
    getTotalNumberObjects: function () {
      return axios({
        method: "GET",
        url: `${process.env.VUE_APP_DXA_ENDPOINT}/items/count?hash=${process.env.VUE_APP_API_HASH}`,
      }).then(
        (result) => {
          this.total = result.data.total;
        },
        (error) => {
          console.error(error);
        }
      );
    },
    // Used in watcher
    setSearchInput: function (query) {
      if (query === "all-objects") {
        return "";
      } else {
        return query;
      }
    },
    getObjects: function () {
      let baseURL = `${process.env.VUE_APP_DXA_ENDPOINT}/items?hash=${process.env.VUE_APP_API_HASH}`;
      let q = "&q=";
      // Encode query before use
      let query = encodeURIComponent(this.$route.query.q);
      let fullURL;
      if (query === "all-objects") {
        if (this.$route.query.page) {
          let page = `&page=${this.$route.query.page}`;
          fullURL = baseURL + page;
        } else {
          fullURL = baseURL;
        }
      } else if (this.$route.query.page) {
        let page = `&page=${this.$route.query.page}`;
        fullURL = baseURL + q + query + page;
      } else {
        fullURL = baseURL + q + query;
      }
      return axios({ method: "GET", url: fullURL }).then(
        (result) => {
          // Sort by startDate key to order items chronologically
          let sorted = Object.values(result.data.items).sort(function (a, b) {
            return a.startDate - b.startDate;
          });
          this.results = sorted;
          // Main object is set to first object initially
          this.mainObject = this.results[0];
          this.pageInfo = result.data.page;
          this.pages = result.data.links;
          this.setResultsExist();
          this.setDisplayPageNavigation();
          this.loaded = true;
        },
        (error) => {
          this.$router.replace({ name: "error" });
          console.error(error);
        }
      );
    },
    setResultsExist: function () {
      if (Object.entries(this.results).length === 0) {
        this.resultsExist = false;
      } else {
        this.resultsExist = true;
      }
    },
    submit: function (e) {
      let query;
      // If query is blank, show all objects
      if (this.searchInput === "") {
        query = "all-objects";
      } else {
        query = this.searchInput;
      }
      if (e.target.name === "search") {
        this.$router.push({ name: "search-results", query: { q: query } });
      } else if (e.target.name === "reset") {
        this.$router.push({
          name: "search-results",
          query: { q: "all-objects" },
        });
      }
    },
    navigatePage: function (page) {
      let url;
      if (page === "first") {
        url = this.pages[0].href;
      } else if (page === "last") {
        url = this.pages[1].href;
      } else if (page === "next") {
        url = this.pages[2].href;
      } else if (page === "previous") {
        url = this.pages[3].href;
      }
      return axios({ method: "GET", url: url }).then(
        (result) => {
          this.results = result.data.items;
          this.pageInfo = result.data.page;
          this.pages = result.data.links;
          let currentQuery = JSON.parse(JSON.stringify(this.$route.query));
          let updateQuery = currentQuery;
          updateQuery["page"] = this.pageInfo.currentPage;
          this.$router.push({ name: "search-results", query: updateQuery });
        },
        (error) => {
          console.error(error);
        }
      );
    },
    // Navigate directly to a page by clicking a number
    navigatePageDirect: function (page) {
      let currentQuery = JSON.parse(JSON.stringify(this.$route.query));
      let updateQuery = currentQuery;
      updateQuery["page"] = page;
      this.$router.push({ name: "search-results", query: updateQuery });
    },
  },
  created() {
    this.searchInput = this.setSearchInput(this.$route.query.q);
    this.getObjects();
    this.getTotalNumberObjects();
  },
};
</script>



<style scoped lang="scss">
.bold {
  font-weight: bold;
}
.italic {
  font-style: italic;
}

#search-results-container {
  width: 100%;
  background-color: white;
}

#back {
  @include flex-center;
  justify-content: flex-start;
  height: 50px;
  padding: 30px 50px 0 50px;
}
#back .fas {
  font-size: 75%;
  padding-right: 4px;
}
#back button {
  @include button-styles;
  padding: 2px;
  font-weight: bold;
}

.loader-container {
  min-height: 500px;
}

#search-header-container {
  @include flex-center;
  width: 100%;
  padding: 0 50px;
}
#search-info-container {
  display: flex;
  justify-content: flex-start;
  width: 50%;
}
#search-info {
  padding-top: 10px;
}
#search-info span {
  font-weight: bold;
}
#results-search-container {
  display: flex;
  flex-direction: column;
  width: 50%;
}
#results-search-container form {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}
#results-search-input {
  width: 50%;
  border: 2px solid $theme-light;
  border-radius: 4px;
  padding: 5px 5px;
  margin-right: 5px;
}
#search-buttons-container {
  display: flex;
}
.results-search-button {
  @include button-styles;
  min-width: 70px;
  background-color: $theme-light;
  border: 2px solid $theme-light;
  border-radius: 4px;
  color: $light-text;
  padding: 5px 8px;
}
#results-search-submit {
  margin-right: 5px;
}
#how-to-link-container {
  align-self: flex-end;
  margin: 5px 0;
}
#how-to-link-container a {
  @include link-styles;
  font-size: 85%;
  font-weight: bold;
}
#how-to-link-container a:hover {
  @include link-hover-styles;
}

#results-wrapper {
  @include flex-center;
  align-items: flex-start;
  padding: 0 50px;
}

#results-main-object {
  width: 25%;
  padding: 30px 0;
}
#main-object-photo-container {
  margin-bottom: 15px;
}
#main-object-image {
  @include image-scale;
  object-fit: contain;
  max-height: 400px;
}
#main-object-name-date {
  margin-bottom: 15px;
}

#results-container {
  width: 75%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px 30px;
  padding: 30px 0 30px 40px;
}
.result {
  @include flex-center;
  justify-content: flex-start;
  height: 120px;
}
.result a {
  text-decoration: none;
  color: black;
}
.result-image {
  @include image-scale;
  object-fit: contain;
  height: 120px;
  width: 150px;
  border: 1px solid $background-color;
}
.result-details {
  @include flex-center;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  height: 100%;
  padding-left: 10px;
}
.result-details a {
  margin-bottom: 5px;
}
.result-details a:hover {
  @include link-hover-styles;
}

#search-no-results {
  padding: 50px 50px 0 50px;
}
#search-no-results button {
  @include button-styles;
  @include link-styles;
}
#search-no-results button:hover {
  @include link-hover-styles;
}
#no-results-text p {
  margin-bottom: 15px;
}

#results-padding {
  margin-bottom: 75px;
}

/* IE-only */
@media only screen and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  #results-container {
    display: -ms-grid;
    -ms-grid-columns: 1fr 30px 1fr;
  }
  .result-1,
  .result-3,
  .result-5,
  .result-7,
  .result-9,
  .result-11,
  .result-13,
  .result-15,
  .result-17,
  .result-19 {
    -ms-grid-column: 1;
  }
  .result-2,
  .result-4,
  .result-6,
  .result-8,
  .result-10,
  .result-12,
  .result-14,
  .result-16,
  .result-18,
  .result-20 {
    -ms-grid-column: 3;
  }
  .result-1,
  .result-2 {
    -ms-grid-row: 1;
  }
  .result-3,
  .result-4 {
    -ms-grid-row: 2;
  }
  .result-5,
  .result-6 {
    -ms-grid-row: 3;
  }
  .result-7,
  .result-8 {
    -ms-grid-row: 4;
  }
  .result-9,
  .result-10 {
    -ms-grid-row: 5;
  }
  .result-11,
  .result-12 {
    -ms-grid-row: 6;
  }
  .result-13,
  .result-14 {
    -ms-grid-row: 7;
  }
  .result-15,
  .result-16 {
    -ms-grid-row: 8;
  }
  .result-17,
  .result-18 {
    -ms-grid-row: 9;
  }
  .result-19,
  .result-20 {
    -ms-grid-row: 10;
  }
  .result-1,
  .result-2,
  .result-3,
  .result-4,
  .result-5,
  .result-6,
  .result-7,
  .result-8,
  .result-9,
  .result-10,
  .result-11,
  .result-12,
  .result-13,
  .result-14,
  .result-15,
  .result-16,
  .result-17,
  .result-18,
  .result-19,
  .result-20 {
    margin-bottom: 15px;
  }
  .result-image-wrapper {
    position: relative;
    height: 120px;
    width: 150px;
    border: 1px solid $background-color;
  }
  .result-image {
    max-height: 100%;
    max-width: 100%;
    height: auto;
    width: auto;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    border: 1px solid transparent;
  }
  .result-details {
    width: calc(100% - 150px - 10px);
  }
  .result-details a,
  .result-details p {
    width: 100%;
  }
}

@media only screen and (max-width: 1199px) {
  #results-search-input {
    width: 60%;
  }
  .results-search-button {
    width: 16%;
  }
  .result {
    height: 175px;
  }
  .result-image {
    height: 175px;
    width: 120px;
  }
}

/* IE-only */
@media only screen and (-ms-high-contrast: none) and (max-width: 1199px),
  (-ms-high-contrast: active) and (max-width: 1199px) {
  .result-image-wrapper {
    height: 175px;
    width: 120px;
  }
  .result-image {
    height: auto;
    width: auto;
  }
  .result-details {
    width: calc(100% - 120px - 10px);
  }
}

@media only screen and (max-width: 974px) {
  #search-header-container {
    flex-direction: column;
    align-items: flex-start;
  }
  #search-info-container {
    width: auto;
  }
  #results-search-container {
    width: auto;
    margin-top: 20px;
  }
  #results-search-container form {
    justify-content: flex-start;
  }
  #results-search-input {
    min-width: 225px;
  }
  #how-to-link-container {
    align-self: flex-start;
  }
  #results-container {
    width: 100%;
    padding-left: 0;
  }
  .result {
    height: 135px;
  }
  .result-image {
    height: 135px;
    width: 150px;
  }
}

/* IE-only */
@media only screen and (-ms-high-contrast: none) and (max-width: 974px),
  (-ms-high-contrast: active) and (max-width: 974px) {
  .result-image-wrapper {
    height: 135px;
    width: 150px;
  }
  .result-image {
    height: auto;
    width: auto;
  }
  .result-details {
    width: calc(100% - 150px - 10px);
  }
}

@media only screen and (max-width: 849px) {
  // Increase height to make more room for text
  .result {
    height: 175px;
  }
  .result-image {
    height: 175px;
  }
}

/* IE-only */
@media only screen and (-ms-high-contrast: none) and (max-width: 849px),
  (-ms-high-contrast: active) and (max-width: 849px) {
  .result-image-wrapper {
    height: 175px;
  }
  .result-image {
    height: auto;
  }
}

@media only screen and (max-width: 724px) {
  #results-container {
    @include flex-center;
    flex-direction: column;
    align-items: flex-start;
    padding-bottom: 20px;
  }
  .result-wrapper {
    width: 100%;
  }
  .result {
    height: 200px;
    width: 100%;
    margin-bottom: 20px;
  }
  .result a {
    width: 38%;
  }
  .result-image {
    height: 200px;
    width: 100%;
  }
  .result-details {
    width: 62%;
  }
}

/* IE-only */
@media only screen and (-ms-high-contrast: none) and (max-width: 724px),
  (-ms-high-contrast: active) and (max-width: 724px) {
  .result-image-wrapper {
    height: 200px;
    width: 100%;
  }
  .result-image {
    height: auto;
    width: auto;
  }
}

@media only screen and (max-width: 599px) {
  #back {
    padding: 30px 30px 0 30px;
  }
  #search-header-container {
    padding: 0 30px;
  }
  #results-wrapper {
    padding: 0 30px;
  }
  .result-details a {
    width: 100%;
  }
  #search-no-results {
    padding: 30px 30px 0 30px;
  }
}

@media only screen and (max-width: 499px) {
  #results-search-container {
    width: 100%;
  }
  #results-search-container form {
    @include flex-center;
    flex-direction: column;
    width: 100%;
  }
  #results-search-input {
    min-width: 0;
    width: 100%;
    margin-right: 0;
  }
  #search-buttons-container {
    justify-content: flex-start;
    width: 100%;
    margin-top: 10px;
  }
  .results-search-button {
    padding: 4px 8px;
  }
  .result {
    flex-direction: column;
    height: 250px;
  }
  .result a {
    height: 65%;
    width: 100%;
  }
  .result-image {
    height: 100%;
  }
  .result-details {
    height: 35%;
    width: 100%;
    padding-left: 0;
    margin-top: 10px;
  }
  .result-details a {
    height: auto;
  }
}

@media only screen and (max-width: 399px) {
  .result {
    height: 225px;
  }
}
</style>